// Feature Flags slice - matching admin-web structure
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  FeatureFlagState,
  FeatureFlagAttributes,
  FeatureFlagType,
  UpdateFeatureFlagPayload,
  ResetFeatureFlagPayload,
  FetchOrganizationFlagsResponse,
} from '@/types/admin/featureFlags';
import { RootState } from '../../index';
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { AppConfig } from '@/constants/residents/config';

const initialState: FeatureFlagState = {
  flags: [],
  isLoading: false,
  error: null,
  selectedFlag: null,
  selectedOrganizationId: null,
  filters: {
    search: '',
  },
};

// Helper function to make authenticated API calls
const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}) => {
  const token = await SecureStorage.getInstance().getToken();
  
  const response = await fetch(`${AppConfig.API.BASE_URL}${url}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

// Async thunks
export const resetFeatureFlag = createAsyncThunk(
  'featureFlags/resetFeatureFlag',
  async (payload: ResetFeatureFlagPayload, { dispatch }) => {
    try {
      await makeAuthenticatedRequest('/api/v1/feature-flags/reset', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      // Fetch updated flags based on context
      if (payload.organizationId) {
        const response = await makeAuthenticatedRequest(
          `/api/v1/feature-flags/organization/${payload.organizationId}`
        );

        if (response?.response) {
          return {
            success: true,
            message: `Successfully reset feature flag ${payload.key}`,
            updatedFlags: response.response,
          };
        }
      } else if (payload.propertyId) {
        const response = await makeAuthenticatedRequest(
          `/api/v1/feature-flags?propertyId=${payload.propertyId}`
        );

        if (response?.response) {
          return {
            success: true,
            message: `Successfully reset feature flag ${payload.key}`,
            updatedFlags: response.response,
          };
        }
      }

      return {
        success: true,
        message: `Successfully reset feature flag ${payload.key}`,
      };
    } catch (error: any) {
      throw new Error(`Failed to reset feature flag ${payload.key}: ${error.message}`);
    }
  }
);

export const updateOrganizationFlags = createAsyncThunk(
  'featureFlags/updateOrganizationFlags',
  async ({ organizationId, flags }: { organizationId: number; flags: FeatureFlagType }) => {
    try {
      const response = await makeAuthenticatedRequest('/api/v1/feature-flags', {
        method: 'POST',
        body: JSON.stringify({ organizationId, flags }),
      });

      if (!response || !response.success) {
        throw new Error(response?.message || 'Failed to update organization flags');
      }

      return {
        organizationId,
        flags: response.response.flags || flags,
      };
    } catch (error: any) {
      console.error('Update organization flags error:', error);
      throw error;
    }
  }
);

export const updatePropertyFlags = createAsyncThunk(
  'featureFlags/updatePropertyFlags',
  async ({ propertyId, flags }: { propertyId: number; flags: FeatureFlagType }) => {
    try {
      const response = await makeAuthenticatedRequest('/api/v1/feature-flags', {
        method: 'POST',
        body: JSON.stringify({
          propertyId: propertyId.toString(),
          flags,
        }),
      });

      if (!response || !response.success) {
        throw new Error(response?.message || 'Failed to update property flags');
      }

      return { propertyId, flags };
    } catch (error: any) {
      console.error('Update property flags error:', error);
      throw error;
    }
  }
);

export const fetchOrganizationFlags = createAsyncThunk(
  'featureFlags/fetchOrganizationFlags',
  async (organizationId: number) => {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    try {
      const response = await makeAuthenticatedRequest(
        `/api/v1/feature-flags/organization/${organizationId}`
      );

      if (!response || !response.success) {
        throw new Error('Failed to fetch organization flags');
      }

      return {
        id: response.response.id,
        organizationId: response.response.id,
        organizationName: response.response.organizationName,
        flags: response.response.flags || {},
        updatedAt: new Date().toISOString(),
      };
    } catch (error: any) {
      console.error('Fetch organization flags error:', error);
      throw error;
    }
  }
);

// Slice
export const featureFlagsSlice = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {
    setSelectedFlag: (state, action: PayloadAction<FeatureFlagAttributes | null>) => {
      state.selectedFlag = action.payload;
    },
    setSelectedOrganizationId: (state, action: PayloadAction<number | null>) => {
      state.selectedOrganizationId = action.payload;
    },
    updateFilters: (state, action: PayloadAction<Partial<typeof initialState.filters>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    resetFeatureFlags: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Reset feature flag
      .addCase(resetFeatureFlag.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetFeatureFlag.fulfilled, (state, action) => {
        state.isLoading = false;

        if (action.payload?.updatedFlags && state.selectedFlag) {
          state.selectedFlag = {
            ...state.selectedFlag,
            flags: action.payload.updatedFlags.flags || {},
            // Don't set updatedAt - optional field that causes Redux serialization warning
            id: state.selectedFlag.id,
            organization_id: state.selectedFlag.organization_id,
            property_id: state.selectedFlag.property_id,
            createdAt: state.selectedFlag.createdAt,
          };

          // Update flags list if organization
          if (action.meta.arg.organizationId) {
            state.flags = state.flags.map((flag) => {
              if (flag.organization_id === action.meta.arg.organizationId) {
                return {
                  ...flag,
                  flags: action.payload.updatedFlags.flags || {},
                  // Don't set updatedAt - optional field that causes Redux serialization warning
                };
              }
              return flag;
            });
          }
        }
      })
      .addCase(resetFeatureFlag.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to reset feature flag';
      })
      // Update organization flags
      .addCase(updateOrganizationFlags.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrganizationFlags.fulfilled, (state, action) => {
        state.isLoading = false;
        state.flags = state.flags.map((flag) => {
          if (flag.organization_id === action.payload.organizationId) {
            return {
              ...flag,
              flags: {
                ...flag.flags,
                ...action.payload.flags,
              },
            };
          }
          return flag;
        });
      })
      .addCase(updateOrganizationFlags.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update organization flags';
      })
      // Update property flags
      .addCase(updatePropertyFlags.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePropertyFlags.fulfilled, (state, action) => {
        state.isLoading = false;
        state.flags = state.flags.map((flag) => {
          if (flag.property_id === action.payload.propertyId) {
            return {
              ...flag,
              flags: {
                ...flag.flags,
                ...action.payload.flags,
              },
            };
          }
          return flag;
        });
      })
      .addCase(updatePropertyFlags.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update property flags';
      })
      // Fetch organization flags
      .addCase(fetchOrganizationFlags.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationFlags.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedFlag = {
          id: action.payload.id,
          organization_id: action.payload.organizationId,
          organizationName: action.payload.organizationName,
          flags: action.payload.flags,
          property_id: undefined,
          // Don't set dates - they're optional and Date objects cause Redux serialization warnings
        };
      })
      .addCase(fetchOrganizationFlags.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch organization flags';
      });
  },
});

export const { setSelectedFlag, setSelectedOrganizationId, updateFilters, resetFeatureFlags } =
  featureFlagsSlice.actions;

// Selectors
export const selectFeatureFlags = (state: RootState) => state.featureFlags.flags;
export const selectFeatureFlagsLoading = (state: RootState) => state.featureFlags.isLoading;
export const selectFeatureFlagsError = (state: RootState) => state.featureFlags.error;
export const selectSelectedFlag = (state: RootState) => state.featureFlags.selectedFlag;
export const selectFeatureFlagFilters = (state: RootState) => state.featureFlags.filters;

export default featureFlagsSlice.reducer;

